import React, { useState, useEffect, useMemo } from 'react';
import Select from 'react-select';
import { useSelectStyles } from '../../utils/selectStyles';
import DNChart from './DNChart';
import DNProductPresence from './DNProductPresence';
import { API_BASE_URL } from '../../config';
import { useDN } from '../../contexts/DNContext';
import LoadingSpinner from '../LoadingSpinner';
import { addMonths, addYears, differenceInMonths, differenceInYears } from 'date-fns';
import useAnalytics from '../../hooks/useAnalytics';
import { Menu } from '../ui/Menu';
import DNProductStoresDetail from './DNProductStoresDetail';
import { RiShieldStarLine } from 'react-icons/ri';
import { _t } from '../../utils/i18n';

const timeGroupingOptions = [
  { id: 'week', label: 'Weekly', maxRange: { value: 1, unit: 'years' } },
  { id: 'month', label: 'Monthly', maxRange: { value: 3, unit: 'years' } }
];

const DNProductDetail = ({
  selectedProject,
  fetchWithAuth,
  selectedShopSigns,
  selectedCategories,
  currentUser,
  defaultProduct,
  obfuscate
}) => {
  const projectId = selectedProject.value;

  const [loading, setLoading] = useState(false);
  const [chartLoading, setChartLoading] = useState(false);
  const [timeGrouping, setTimeGrouping] = useState('week');
  const [rawData, setRawData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const { products, startDate, endDate, setStartDate, issues } = useDN();
  const [selectedProducts, setSelectedProducts] = useState([]);
  const selectStyles = useSelectStyles();
  const [activeTab, setActiveTab] = useState('evolution');

  // reset selectedProducst if selectedProject change
  useEffect(() => {
    setSelectedProducts([]);
  }, [selectedProject]);

  // look for productId in the url
  useEffect(() => {
    if (!selectedProject) return;
    if (!defaultProduct) return;

    // L'effet ne devrait s'exécuter qu'une seule fois au chargement initial,
    // pas lorsque selectedProducts change
    if (selectedProducts.length !== 0) return;

    // Vérifiez que le selectedProducts n'a pas encore été défini par l'utilisateur
    const isInitialRender = selectedProducts.length === 0;

    if (isInitialRender) {
      const product = products.find(product => product.value === defaultProduct);
      if (product) {
        setSelectedProducts([product]);
      }
    }
  }, [products, defaultProduct, selectedProject]); // Retirez selectedProducts.length des dépendances

  const { track } = useAnalytics({ currentUser, project: selectedProject.label });

  // Helper pour ajuster les dates selon le groupement
  const handleTimeGroupingChange = (newGrouping) => {
    const groupingOption = timeGroupingOptions.find(option => option.id === newGrouping);
    if (!groupingOption) return;

    const { maxRange } = groupingOption;
    let isRangeValid = true;
    let adjustedStartDate = null;

    if (maxRange.unit === 'months') {
      isRangeValid = differenceInMonths(endDate, startDate) <= maxRange.value;
      if (!isRangeValid) {
        adjustedStartDate = addMonths(endDate, -maxRange.value);
      }
    } else if (maxRange.unit === 'years') {
      isRangeValid = differenceInYears(endDate, startDate) <= maxRange.value;
      if (!isRangeValid) {
        adjustedStartDate = addYears(endDate, -maxRange.value);
      }
    }

    // Mettre à jour le groupement
    setTimeGrouping(newGrouping);

    // Ajuster la date de début si nécessaire
    if (adjustedStartDate) {
      setStartDate(adjustedStartDate);
    }
  };

  useEffect(() => {
    if (!selectedProducts.length) return;

    track('dn_product_detail_view', {
      dn_view_products: selectedProducts.map(product => product.value).join(', ')
    });
  }, [selectedProducts, track]);

  // Fetch data when necessary
  useEffect(() => {
    const fetchData = async () => {
      if (!projectId || !selectedProducts.length) return;

      try {
        if (rawData.length > 0) {
          setChartLoading(true);
        } else {
          setLoading(true);
        }

        const formattedStartDate = startDate.toISOString().split('T')[0];
        const formattedEndDate = endDate.toISOString().split('T')[0];

        // Construire l'URL avec les produits[] sélectionnés
        let url = `${API_BASE_URL}/api/dwh/dn/${projectId}/products/measures?startDate=${formattedStartDate}&endDate=${formattedEndDate}&forVariations=true`;

        // Ajouter chaque produit sélectionné à l'URL
        selectedProducts.forEach(product => {
          url += `&products[]=${encodeURIComponent(product.value)}`;
        });

        const response = await fetchWithAuth(url);

        if (response.ok) {
          const measuresData = await response.json();
          const filteredMeasuresData = measuresData.filter(
            ({ reason, isPresent }) => typeof reason === 'string' || isPresent === true
          );
          setRawData(filteredMeasuresData);
        }
      } catch (error) {
        console.error("Error fetching measures:", error);
      } finally {
        setLoading(false);
        setChartLoading(false);
      }
    };

    fetchData();
  }, [projectId, selectedProducts, startDate, endDate, timeGrouping, fetchWithAuth, rawData.length]);

  // Appliquer le filtrage par enseignes et catégories
  useEffect(() => {
    if (!rawData.length) {
      setFilteredData([]);
      return;
    }

    // Filtrer par enseigne si des enseignes sont sélectionnées
    let filtered = rawData;

    if (selectedShopSigns && selectedShopSigns.length > 0) {
      const shopSignValues = selectedShopSigns.map(sign => sign.value);
      filtered = filtered.filter(item =>
        shopSignValues.includes(item.shopSign)
      );
    }

    // Filtrer par catégorie si des catégories sont sélectionnées
    if (selectedCategories && selectedCategories.length > 0) {
      const categoryValues = selectedCategories.map(cat => cat.value);
      filtered = filtered.filter(item =>
        categoryValues.includes(item.category)
      );
    }

    setFilteredData(filtered);
  }, [rawData, selectedShopSigns, selectedCategories]);

  // Extraire les issues pour les produits sélectionnés
  const filteredIssues = useMemo(() => {
    if (!issues.length || !selectedProducts.length) return [];

    // Filtre pour ne garder que les issues des produits sélectionnés
    const productNames = selectedProducts.map(p => p.value);
    let result = issues.filter(issue => productNames.includes(issue.productName));

    // Appliquer les filtres d'enseignes si nécessaire
    if (selectedShopSigns && selectedShopSigns.length > 0) {
      const shopSignValues = selectedShopSigns.map(sign => sign.value);
      result = result.filter(issue => shopSignValues.includes(issue.shopSign));
    }

    // Appliquer les filtres de catégories si nécessaire
    if (selectedCategories && selectedCategories.length > 0) {
      const categoryValues = selectedCategories.map(cat => cat.value);
      result = result.filter(issue => categoryValues.includes(issue.category));
    }

    return result;
  }, [issues, selectedProducts, selectedShopSigns, selectedCategories]);

  // Afficher un message si les données sont vides après filtrage
  const noDataAfterFiltering = !loading && rawData.length > 0 && filteredData.length === 0;

  const menuItems = [
    {
      id: 'evolution',
      label: _t('ND evolution'),
      icon: (
        <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4h16v16H4V4z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 12h4l3-3 4 6 3-3h2" />
        </svg>
      )
    },
    {
      id: 'details',
      label: _t('Store presence'),
      icon: (
        <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
      )
    },
    {
      id: 'presence',
      label: _t('Variations'),
      icon: (
        <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h12a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V6z" />
          <path strokeLinecap="round" strokeWidth={2} d="M8 10h2v2H8v-2zm4 0h2v2h-2v-2zm-4 4h2v2H8v-2zm4 0h2v2h-2v-2z" />
        </svg>
      ),
      admin: false
    }
  ];

  // Ajoutez cet élément pour le badge admin
  const AdminBadge = () => (
    <span className="ml-2 px-1.5 py-0.5 text-xs font-semibold rounded bg-purple-100 text-purple-800 dark:bg-purple-800 dark:text-purple-100 flex items-center">
      <RiShieldStarLine className="mr-1" />
      Admin
    </span>
  );

  // Filtrez les éléments du menu en fonction des droits d'administration
  const filteredMenuItems = useMemo(() => {
    return menuItems.filter(item => {
      // Si l'élément requiert des droits admin, vérifiez si l'utilisateur est admin
      if (item.admin) {
        return currentUser?.isadmin === true;
      }
      // Sinon, affichez toujours l'élément
      return true;
    }).map(item => {
      // Ajoutez le badge admin si nécessaire
      if (item.admin) {
        return {
          ...item,
          badge: <AdminBadge />
        };
      }
      return item;
    });
  }, [menuItems, currentUser]);

  const renderTabContent = () => {
    if (loading) {
      return <LoadingSpinner />;
    }

    if (noDataAfterFiltering) {
      return (
        <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow flex flex-col items-center justify-center text-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">
            {_t('No data available')}
          </h3>
          <p className="text-gray-500 dark:text-gray-400">
            {_t('The selected products have no data corresponding to the current filters')}
          </p>
        </div>
      );
    }

    if (selectedProducts.length === 0) {
      return (
        <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow flex flex-col items-center justify-center text-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">
            {_t('Please select at least one product')}
          </h3>
          <p className="text-gray-500 dark:text-gray-400">
            {_t('Choose up to 5 products to display their distribution data')}
          </p>
        </div>
      );
    }

    if (activeTab === 'evolution') {
      return (
        <div className="relative">
          {chartLoading && (
            <div className="absolute inset-0 bg-white/80 dark:bg-gray-800/80 z-10 flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          )}
          <DNChart
            selectedProject={selectedProject}
            fetchWithAuth={fetchWithAuth}
            selectedProducts={selectedProducts}
            selectedShopSigns={selectedShopSigns}
          />
        </div>
      );
    }

    if (activeTab === 'presence') {
      return (
        <div>
          <DNProductPresence
            data={rawData}
            startDate={startDate}
            endDate={endDate}
          />
        </div>
      );
    }

    if (activeTab === 'details') {
      return (
        <div>
          <DNProductStoresDetail
            selectedProject={selectedProject}
            selectedProducts={selectedProducts}
            fetchWithAuth={fetchWithAuth}
            startDate={startDate}
            endDate={endDate}
          />
        </div>
      );
    }

    return null;
  };

  return (
    <div className="sm:p-4 p-2">
      {/* Layout avec sidebar et contenu */}
      <div className="flex flex-col md:flex-row gap-6">
        {/* Sidebar - Utiliser filteredMenuItems au lieu de menuItems */}
        <div className="w-full md:w-60 shrink-0">
          <Menu
            items={filteredMenuItems}
            activeTab={activeTab}
            onTabClick={setActiveTab}
          />
        </div>

        {/* Contenu principal */}
        <div className="flex-1 bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
          <div className="pb-4 bg-gray-50 dark:bg-gray-800">
            {/* Product and Time Grouping Selection */}
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="w-full">
                <Select
                  className="react-select-container"
                  classNamePrefix="react-select"
                  options={obfuscate ? products.map(product => ({
                    ...product,
                    label: `${product.label.slice(0, 2)}${'*'.repeat(Math.max(0, product.label.length - 2))}`
                  })) : products}
                  value={obfuscate ? selectedProducts.map(product => ({
                    ...product,
                    label: `${product.label.slice(0, 2)}${'*'.repeat(Math.max(0, product.label.length - 2))}`
                  })) : selectedProducts}
                  onChange={(selected) => setSelectedProducts(selected ? selected.slice(0, 5) : [])}
                  placeholder={_t('Select products (max 5)...')}
                  isMulti
                  isClearable={true}
                  styles={selectStyles}
                  noOptionsMessage={() => _t('No product found')}
                />
                {selectedProducts.length >= 5 && (
                  <p className="mt-1 text-xs text-orange-500">
                    {_t('Maximum 5 products can be selected simultaneously')}
                  </p>
                )}
              </div>

              {/* <div className="w-full lg:w-1/3">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Groupement
                </label>
                <TimeGroupingSelector
                  value={timeGrouping}
                  onChange={handleTimeGroupingChange}
                  options={timeGroupingOptions}
                />
              </div> */}
            </div>

            {/* Filters information */}
            {(selectedShopSigns?.length > 0 || selectedCategories?.length > 0) && selectedProducts.length > 0 && (
              <div className="mt-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-md p-2 text-sm">
                <p className="text-blue-800 dark:text-blue-200">
                  {selectedShopSigns?.length > 0 && `Filtré par ${selectedShopSigns.length} enseigne(s)`}
                  {selectedShopSigns?.length > 0 && selectedCategories?.length > 0 && ' et '}
                  {selectedCategories?.length > 0 && `${selectedCategories.length} catégorie(s)`}
                </p>
              </div>
            )}
          </div>

          {/* Content area with padding */}
          <div className="overflow-auto">
            {renderTabContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DNProductDetail;
